// apps/web/src/features/cargo/ability.ts
//
// Per-ROW Cargo ability, derived client-side from the current actor + the
// row's responsible manager (#1232). `CargoArticleWire` carries NO ability
// flags, so these mirror the #1228/#1230 server authz EXACTLY — the backend
// stays authoritative (a wrong guess still 403/409s and surfaces a toast);
// this is only an affordance gate so the UI never offers a control the server
// will refuse.
//
// WHY a dedicated helper: the cohorts mix permission CODES with per-row OWNER
// scope and role-name exclusions (e.g. the client-tariff edit hides for
// logistic roles). Encapsulating the rules here keeps the modals/table
// declarative and the policy unit-testable.

import type { CargoArticleWire, PermissionCode, User } from '@magimp/shared';

import { hasPermission, type PermissionsBundle } from '../../stores/auth.js';

/** The current actor as the ability helpers consume it. */
export interface CargoActor {
  user: User | null;
  permissions: PermissionsBundle;
}

const LOGISTIC_ROLE_NAMES = new Set(['logist_cn', 'logist_ru']);

function roleName(user: User | null): string | null {
  return user && user.role ? user.role.name : null;
}

function isAdmin(user: User | null): boolean {
  return roleName(user) === 'admin';
}

function isLogisticRole(user: User | null): boolean {
  const name = roleName(user);
  return name !== null && LOGISTIC_ROLE_NAMES.has(name);
}

function can(actor: CargoActor, code: PermissionCode): boolean {
  return hasPermission(actor.permissions, code);
}

/**
 * WHY: the JWT subject (`sub`) equals `User.id`; the row stores the owner as
 * `responsibleManagerId`. "Responsible actor" means the current user owns the
 * row. An unassigned row (null owner) is owned by nobody.
 */
function isResponsibleActor(actor: CargoActor, row: CargoArticleWire): boolean {
  const uid = actor.user?.id ?? null;
  return uid !== null && row.responsibleManagerId === uid;
}

/**
 * Row edit modal — logistics+admin (`admin`/`logist_ru`/`logist_cn`) gated on
 * `cargo.update`. manager/rop cannot edit. Row click + the article cell open
 * the edit modal only when this is true.
 */
export function canEditCargoRow(actor: CargoActor): boolean {
  return (isAdmin(actor.user) || isLogisticRole(actor.user)) && can(actor, 'cargo.update');
}

/** Read-only row card — anyone allowed to read the Cargo registry. */
export function canReadCargoRow(actor: CargoActor): boolean {
  return can(actor, 'cargo.read');
}

/** Manual add — same logistics+admin cohort, gated on `cargo.create`. */
export function canCreateCargoRow(actor: CargoActor): boolean {
  return (isAdmin(actor.user) || isLogisticRole(actor.user)) && can(actor, 'cargo.create');
}

/** Soft-delete — logistics+admin, gated on `cargo.delete`. */
export function canDeleteCargoRow(actor: CargoActor): boolean {
  return (isAdmin(actor.user) || isLogisticRole(actor.user)) && can(actor, 'cargo.delete');
}

/**
 * China-payment field inside the edit modal — editable only for `admin` /
 * `logist_cn` (code `cargo.chinaPayment.manage`); READ-ONLY for `logist_ru`.
 */
export function canManageChinaPayment(actor: CargoActor): boolean {
  return (
    (isAdmin(actor.user) || roleName(actor.user) === 'logist_cn') &&
    can(actor, 'cargo.chinaPayment.manage')
  );
}

/** Carrier-payment status field inside the edit modal — code `cargo.carrierPayment.manage`. */
export function canManageCarrierPayment(actor: CargoActor): boolean {
  return can(actor, 'cargo.carrierPayment.manage');
}

/**
 * Client TARIFF edit (the client transportation-tariff column) — code `cargo.clientTariff.manage`
 * AND row scope: admin any row; manager/rop ONLY their own responsible rows.
 * Logistic roles do NOT get it.
 */
export function canEditClientTariff(actor: CargoActor, row: CargoArticleWire): boolean {
  if (!can(actor, 'cargo.clientTariff.manage')) return false;
  if (isAdmin(actor.user)) return true;
  if (isLogisticRole(actor.user)) return false;
  return isResponsibleActor(actor, row);
}

/**
 * GATED client-payment status (the document modal) — code
 * `cargo.clientPayment.manage`, RESPONSIBLE-ACTOR ONLY. Admin is NOT implicitly
 * allowed here; the gated save also requires ≥1
 * active document (enforced by the modal disabling Save).
 */
export function canSaveGatedClientPayment(actor: CargoActor, row: CargoArticleWire): boolean {
  return can(actor, 'cargo.clientPayment.manage') && isResponsibleActor(actor, row);
}

/**
 * FINANCIER client-payment review (the document drawer's review controls) —
 * permission `cargo.clientPayment.review` ONLY (#1938). Deliberately NOT tied to
 * a role-name cohort or a row-owner scope: a financier reviews any row they can
 * see. The grant currently lives on `financier_ru`, but this gate keys purely on
 * the permission so the policy follows the grant, never a hard-coded role name.
 */
export function canReviewClientPayment(actor: CargoActor): boolean {
  return can(actor, 'cargo.clientPayment.review');
}

/**
 * Payment DOCUMENTS attach/delete — code `cargo.clientPaymentDocs.manage` +
 * row scope (admin any; manager/rop responsible rows). List/download is just
 * `cargo.read`, available to anyone who can see the row (incl. logists).
 */
export function canManagePaymentDocs(actor: CargoActor, row: CargoArticleWire): boolean {
  if (!can(actor, 'cargo.clientPaymentDocs.manage')) return false;
  if (isAdmin(actor.user)) return true;
  return isResponsibleActor(actor, row);
}

/** Import — codes `cargo.import` AND only roles `admin` / `logist_cn`. */
export function canImportCargoFiles(actor: CargoActor): boolean {
  return (
    (isAdmin(actor.user) || roleName(actor.user) === 'logist_cn') && can(actor, 'cargo.import')
  );
}
